import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// Bundled rather than loaded from Google Fonts, so no visitor's IP goes to
// Google and the native apps render the right fonts offline. Latin subsets
// only, to keep the app bundles small; other scripts fall back to sans-serif.
import "@fontsource/bebas-neue/latin-400.css";
import "@fontsource/bebas-neue/latin-ext-400.css";
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-ext-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-ext-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-ext-600.css";
import "@fontsource/inter/latin-700.css";
import "@fontsource/inter/latin-ext-700.css";
import "./index.css";
import App from "./App.tsx";
import { scrubForSentry } from "./utils/sentryScrub";
import { AppErrorFallback } from "./components/AppErrorFallback";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn) {
  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_APP_ENV as string | undefined) || import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE,
    // Replays mask all text by default. Attributes need masking too: links like
    // /crew/<handle> live in href. The hook covers replay breadcrumbs and spans
    // (it never sees DOM snapshots, hence maskAttributes).
    integrations: [
      Sentry.replayIntegration({
        maskAttributes: ["title", "placeholder", "aria-label", "href"],
        beforeAddRecordingEvent: (event) => scrubForSentry(event),
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend: (event) => scrubForSentry(event),
    beforeSendTransaction: (event) => scrubForSentry(event),
    beforeBreadcrumb: (breadcrumb) => scrubForSentry(breadcrumb),
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={({ error }) => <AppErrorFallback error={error} />}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
